import { indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getBrowserOS } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import type { Action } from 'flow/store/engine/types';
import type { WorkflowState } from 'flow/store/redux/state';
import { WEBHOOK_KEY } from '../NodeEditorDrawer/ServiceTabs/constants';

/**
 * Checks if the event target is an input element (input, textarea, or contenteditable)
 */
export function isTargetInputElement(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;

  if (!target) {
    return false;
  }

  return target.tagName === 'INPUT'
    || target.tagName === 'TEXTAREA'
    || target.isContentEditable;
}

/**
 * Returns the keyboard shortcuts for copy and paste based on the user's operating system
 */
export function getKeyboardShortcuts(): { copyShortcut: string; pasteShortcut: string } {
  const browserOS = getBrowserOS();
  const isMac = browserOS === 'mac';

  return {
    copyShortcut: isMac ? '⌘ C' : 'Ctrl C',
    pasteShortcut: isMac ? '⌘ V' : 'Ctrl V',
  };
}

/**
 * Filters workflow props to only include items that are actually used by actions in the snippet.
 */
export function filterPropsUsedByActions({
  actions,
  thresholds,
  workflowProps,
}: {
  actions: WorkflowState['workflowData']['actions'];
  thresholds: WorkflowState['workflowData']['thresholds'];
  workflowProps: WorkflowState['workflowProps'];
}) {
  const actionsArray = Object.values(actions);
  const thresholdsArray = Object.values(thresholds);

  // Collect identifiers for each prop type used in actions
  const usedServiceInternalNames = new Set<string>();
  const usedTagNames = new Set<string>();
  const usedOutcomeNames = new Set<string>();
  const usedPublishedAttributeTokens = new Set<string>();
  const usedReasonCodeGroupNames = new Set<string>();

  actionsArray.forEach(action => {
    switch (action.type) {
      case ActionType.SERVICE:
        if (action.service?.internalName) {
          usedServiceInternalNames.add(action.service.internalName);
        }
        break;

      case ActionType.TAG:
        if (action.tag?.name) {
          usedTagNames.add(action.tag.name);
        }
        break;

      case ActionType.OUTCOME:
        if (action.outcome?.name) {
          usedOutcomeNames.add(action.outcome.name);
        }
        break;

      case ActionType.INPUT:
        if (action.input?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.input.publishedAttributeToken);
        }
        break;

      case ActionType.OUTPUT:
        if (action.output?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.output.publishedAttributeToken);
        }
        break;

      default:
        break;
    }
  });


  thresholdsArray.forEach(threshold => {
    if (threshold.right?.reasonCodeGroup) {
      usedReasonCodeGroupNames.add(threshold.right.reasonCodeGroup.name);
    }
  });


  return {
    services: Object.values(workflowProps.services).filter(
      service => usedServiceInternalNames.has(service.internal_name),
    ),
    tags: workflowProps.tags.filter(
      tag => usedTagNames.has(tag.tag_name),
    ),
    outcomes: workflowProps.outcomes.filter(
      outcome => usedOutcomeNames.has(outcome.outcome),
    ),
    publishedAttributes: workflowProps.publishedAttributes.filter(
      pa => usedPublishedAttributeTokens.has(pa.attributeToken),
    ),
    reasonCodeGroups: workflowProps.reasonCodeGroups.filter(
      rcg => usedReasonCodeGroupNames.has(rcg.name),
    ),
  };
}


const PROPRIETARY_NON_CREDS_SERVICE_ACTION_OPTIONS = [WEBHOOK_KEY];
/**
 * removes a specific service action options that are proprietary to the account but
 * are not related to credentials.
 */
export function scrubServiceActionsProprietaryNonCredOptions(args: {
  actions: Record<string, Action>;
}) {
  const { actions } = args;

  const scrubbedActions = Object.values(actions).map(action => {
    // do not modify non-service actions
    if (!isActionOfType(action, ActionType.SERVICE)) {
      return action;
    }

    const scrubbedActionOptions = Object
      .entries(action.options)
      .filter(([name]) => !PROPRIETARY_NON_CREDS_SERVICE_ACTION_OPTIONS.includes(name));

    return {
      ...action,
      options: Object.fromEntries(scrubbedActionOptions),
    };
  });

  return indexActionsByPublicId(scrubbedActions);
}
